import { useEffect, useState } from 'react';

import useAccountSearch from '../../shared/useAccountSearch';
import { fetchAccounts, fetchFilterOptions } from '../services/usersService';

/**
 * Option lists for the filter menus.
 *
 * Permissions and job roles are bounded, so they load whole and the menus narrow
 * them in the browser. Accounts are not — even inside the Sentinel OEM Segment
 * there are hundreds — so they are searched and paged server side, which is why
 * that menu is the one the design gives a search input.
 *
 * The account paging is useAccountSearch, the same hook the Create User wizard's
 * Accounts field uses. `isOpen` is `true` because this list is not behind a
 * picker: the filter row is on screen from the moment the page mounts.
 *
 * @param {number} refreshKey bumped to re-ask for the option lists (see below)
 */
const useFilterOptions = (refreshKey = 0) => {
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [jobRoleOptions, setJobRoleOptions] = useState([]);

  /**
   * Permissions and job roles, re-asked whenever `refreshKey` changes for the
   * same reason the accounts below are: UserFilterOptions.permissions offers only
   * the roles a listed user actually holds (PD-4409), so creating a user can add
   * an option, and until this refetches the permission the new user was given is
   * missing from the very menu you would filter by to find them.
   */
  useEffect(() => {
    let cancelled = false;

    fetchFilterOptions()
      .then(({ permissions, jobRoles }) => {
        if (cancelled) return;
        setPermissionOptions(permissions);
        setJobRoleOptions(jobRoles);
      })
      // A failed option list leaves the menus empty rather than breaking the
      // table, which is the part of the page that matters.
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  /**
   * Accounts, re-asked whenever `refreshKey` changes.
   *
   * UserFilterOptions.accounts offers only accounts that already have a listable
   * user, so that picking one cannot empty the table. Assigning the first user to
   * an account makes it eligible, and until this refetches the account the new user
   * belongs to is missing from the very menu you would filter by to find them.
   */
  const {
    accountOptions,
    accountsTotal,
    hasMoreAccounts,
    isLoadingAccounts,
    loadMoreAccounts,
    searchAccounts,
  } = useAccountSearch(fetchAccounts, true, refreshKey);

  return {
    accountOptions,
    accountsTotal,
    hasMoreAccounts,
    isLoadingAccounts,
    jobRoleOptions,
    loadMoreAccounts,
    permissionOptions,
    searchAccounts,
  };
};

export default useFilterOptions;
